import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { IonicButton } from 'ionic-ui';
import DialogTitle from '../../../../DialogTitle';
import { RootState } from '../../../../../../store/store';
import { useDialog } from '../../../../../../context/Dialog';
import useIonicToast from '../../../../../../hooks/useIonicToast';
import { setWalletPin } from '../../../../../../services/walletSetup/walletOps/walletOps';
import PinField from './PinField';

type Props = {
  onDone?: () => void;
};

/**
 * Handles both first-time PIN setup and changing an existing PIN in one form.
 * We don't know up front whether the user already has a PIN — the backend
 * tells us via a "Current PIN is required" error, at which point we reveal
 * the current-PIN field and let them retry.
 */
const SetWalletPinDialog: React.FC<Props> = ({ onDone }) => {
  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );
  const { closeDialog } = useDialog();
  const { presentIonToast } = useIonicToast();

  const [needsCurrentPin, setNeedsCurrentPin] = useState(false);
  const [currentPin, setCurrentPin] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      setWalletPin({
        authToken: authToken!,
        pin,
        ...(needsCurrentPin ? { current_pin: currentPin } : {}),
      }),
    onSuccess: () => {
      presentIonToast(
        needsCurrentPin ? 'Wallet PIN updated.' : 'Wallet PIN set successfully.'
      );
      closeDialog({ keepParent: true });
      onDone?.();
    },
    onError: (err: any) => {
      const message = err?.message || 'Could not save PIN. Please try again.';
      if (!needsCurrentPin && /current pin/i.test(message)) {
        setNeedsCurrentPin(true);
        setError('You already have a PIN — enter it to change it.');
        return;
      }
      setError(message);
    },
  });

  const submit = () => {
    setError(null);
    if (needsCurrentPin && currentPin.length !== 6) {
      setError('Enter your current 6-digit PIN.');
      return;
    }
    if (pin.length !== 6) {
      setError('PIN must be exactly 6 digits.');
      return;
    }
    if (pin !== confirmPin) {
      setError('PINs do not match.');
      return;
    }
    mutation.mutate();
  };

  return (
    <div className="space-y-4 sm:max-w-[420px] dialog-content">
      <DialogTitle>
        {needsCurrentPin ? 'Change wallet PIN' : 'Set wallet PIN'}
      </DialogTitle>
      <p className="text-sm medium-emphasis-text">
        Your 6-digit PIN authorizes transfers and withdrawals from your wallet.
        Keep it private — 3 wrong attempts will lock it.
      </p>

      <div className="space-y-3">
        {needsCurrentPin && (
          <PinField
            label="Current PIN"
            value={currentPin}
            onChange={setCurrentPin}
            autoFocus
          />
        )}
        <PinField
          label="New PIN"
          value={pin}
          onChange={setPin}
          autoFocus={!needsCurrentPin}
        />
        <PinField
          label="Confirm new PIN"
          value={confirmPin}
          onChange={setConfirmPin}
        />
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex gap-2 justify-end">
        <IonicButton
          variant="outline"
          type="button"
          onClick={() => closeDialog({ keepParent: true })}
        >
          Cancel
        </IonicButton>
        <IonicButton
          type="button"
          onClick={submit}
          disabled={mutation.isPending}
        >
          {mutation.isPending
            ? 'Saving…'
            : needsCurrentPin
              ? 'Update PIN'
              : 'Set PIN'}
        </IonicButton>
      </div>
    </div>
  );
};

export default SetWalletPinDialog;
